import * as React from "react";
import { useState } from "react";
import { Button, ButtonGroup, Input, Popover, PopoverBody } from "reactstrap";
import { toast } from "react-toastify";

import { ContentEvalRow, ContentEvalVote } from "../../common/contentTypes";
import * as Content from "../../services/api/Content";

interface Props {
  questionId: string;
  /** The current user's existing eval, when the page preloaded it. */
  existing?: ContentEvalRow;
  context?: "library" | "review";
}

/**
 * One eval per question per reviewer, updated in place. The thumb writes
 * immediately; the popover adds an optional rating and comment, which is what
 * the generation feedback loop reads back as critique examples.
 */
export const EvalControl: React.FC<Props> = ({
  questionId,
  existing,
  context = "library",
}) => {
  const [vote, setVote] = useState<ContentEvalVote | undefined>(existing?.vote);
  const [rating, setRating] = useState<number | undefined>(
    existing?.rating ?? undefined,
  );
  const [comment, setComment] = useState(existing?.comment ?? "");
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);

  const targetId = `content-eval-${questionId.replace(/[^a-zA-Z0-9]/g, "")}`;

  const save = async (
    nextVote: ContentEvalVote,
    extras?: { rating?: number; comment?: string },
  ) => {
    setSaving(true);
    try {
      await Content.upsertEval({
        questionId,
        vote: nextVote,
        rating: extras?.rating,
        comment: extras?.comment?.trim() || undefined,
        context,
      });
      setVote(nextVote);
    } catch (err) {
      toast.error((err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <ButtonGroup size="sm" id={targetId}>
        <Button
          color={vote === "up" ? "success" : "secondary"}
          outline={vote !== "up"}
          disabled={saving}
          title="Good question"
          onClick={() => {
            save("up");
            setOpen(true);
          }}
        >
          <i className="fas fa-thumbs-up" />
        </Button>
        <Button
          color={vote === "down" ? "danger" : "secondary"}
          outline={vote !== "down"}
          disabled={saving}
          title="Needs work"
          onClick={() => {
            save("down");
            setOpen(true);
          }}
        >
          <i className="fas fa-thumbs-down" />
        </Button>
      </ButtonGroup>

      <Popover
        target={targetId}
        isOpen={open}
        placement="left"
        toggle={() => setOpen(false)}
        trigger="legacy"
      >
        <PopoverBody style={{ minWidth: 220 }}>
          <div className="mb-2">
            <Input
              type="select"
              className="custom-select"
              bsSize="sm"
              value={rating ?? ""}
              onChange={(e) =>
                setRating(e.target.value ? Number(e.target.value) : undefined)
              }
            >
              <option value="">Rating (optional)</option>
              {[1, 2, 3, 4, 5].map((value) => (
                <option key={value} value={value}>
                  {value}
                </option>
              ))}
            </Input>
          </div>
          <Input
            type="textarea"
            bsSize="sm"
            rows={2}
            placeholder="Why? (optional)"
            value={comment}
            onChange={(e) => setComment(e.target.value)}
          />
          <Button
            size="sm"
            color="primary"
            className="mt-2"
            disabled={saving || !vote}
            onClick={async () => {
              if (!vote) {
                return;
              }
              await save(vote, { rating, comment });
              setOpen(false);
            }}
          >
            Save detail
          </Button>
        </PopoverBody>
      </Popover>
    </>
  );
};
